import PriceData from '../../model/price-data';
import StockAndPriceData from '../../model/stock-and-price-data';
import { formatPrice } from '../../helpers/utils';
import { BatchFetcher } from '../../helpers/batch-fetcher';
import { pickCheapestVariant } from '../../helpers/pick-cheapest-variant';

type AsyncPriceKey = 'price' | 'priceExclVat';

export type AsyncPriceRequest = {
  productCode: string;
  productVariantCodes?: string;
};

function priceEndpointResolver(initialOrder: boolean): (request: AsyncPriceRequest) => string {
  return () => {
    const path = initialOrder ? '/products/price/initialOrder' : '/products/price';
    return `${window.ACC.config.encodedContextPath}${path}`;
  };
}

const selectors = {
  container: '.js-asyncPrice',
  value: '.js-asyncPriceValue',
};

export default class AsyncPrices {
  static readonly DATA_LOADED_EVENT = 'asyncPriceLoaded';

  private readonly fetcher: BatchFetcher<AsyncPriceRequest, StockAndPriceData>;

  constructor(
    private readonly rows: JQuery = $(selectors.container),
    initialOrder = false,
  ) {
    this.fetcher = new BatchFetcher(priceEndpointResolver(initialOrder));

    this.rows = rows.filter((_, row) => $(row)
      .has(selectors.value).length > 0);

    if (this.rows.length) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.loadAll();
    }
  }

  static init(container: JQuery, initialOrder = false): AsyncPrices {
    return new AsyncPrices(container.find(selectors.container), initialOrder);
  }

  static initAll(initialOrder = false): AsyncPrices {
    return new AsyncPrices($(selectors.container), initialOrder);
  }

  private async loadAll(): Promise<void> {
    const requestData = this.getRequestDataFromRows();
    const withoutVariants = requestData.filter((r) => !r.productVariantCodes);
    const withVariants = requestData.filter((r) => r.productVariantCodes);

    try {
      const codeCount: Record<string, number> = {};

      await Promise.all([
        this.fetcher.fetchChunked(
          withoutVariants,
          (chunk) => {
            chunk.forEach((data) => {
              const { productCode } = data;
              let row = this.rows.filter(`[data-code="${productCode}"]`);
              if (row.length > 1) {
                const idx = codeCount[productCode] ?? 0;
                row = $(row.get(idx)!);
                codeCount[productCode] = idx + 1;
              }
              this.render(row, data);
            });
          },
          (error) => this.handleError(error),
        ),
        ...withVariants.map((p) => this.loadVariants(p)),
      ]);
    } finally {
      this.removePriceLoader();
      document.body.dispatchEvent(new Event(AsyncPrices.DATA_LOADED_EVENT));
    }
  }

  private async loadVariants(product: AsyncPriceRequest): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- products are filtered in loadAll()
    const variantRequests: AsyncPriceRequest[] = product.productVariantCodes!
      .split(',')
      .map((productCode) => ({ productCode }));

    const allData: StockAndPriceData[] = [];

    await this.fetcher.fetchChunked(
      variantRequests,
      (chunk) => allData.push(...chunk),
      (error) => this.handleError(error),
    );

    const best = pickCheapestVariant(allData);
    if (best) {
      const row = this.rows.filter(`[data-code="${product.productCode}"]`);
      this.render(row, best);
    }
  }

  private handleError(error: JQuery.jqXHR): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (error.responseJSON?.length) {
      window.NotificationHelper.clearNotifications();
      window.NotificationHelper.showError(error.responseJSON);
    }
  }

  private getRequestDataFromRows(): AsyncPriceRequest[] {
    const result: AsyncPriceRequest[] = [];
    this.rows.each((_, element) => {
      const { code, productVariantCodes } = element.dataset;
      if (!code) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, no-console
        console.error(`Insufficient details for async price request, productCode: ${code}`);
        return;
      }
      result.push({ productCode: code, productVariantCodes });
    });
    return result;
  }

  private render(row: JQuery, data: StockAndPriceData): void {
    row.find(selectors.value).each((_, fieldEl) => {
      const field = $(fieldEl);
      const key = String(field.data('async-key') ?? '') as AsyncPriceKey;

      if (key === 'price') {
        this.renderPriceValues(field, data.sapPriceWithVat, data.sapPromotionPriceWithVat);
      } else if (key === 'priceExclVat') {
        this.renderPriceValues(field, data.sapPrice, data.sapPromotionPrice);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private renderPriceValues(element: JQuery<HTMLElement>, regularPrice: PriceData | null, promotionPrice: PriceData | null): void {
    const showCurrencyCode = element.data('currency-code') as boolean;

    if (!regularPrice) {
      if (element.data('price-populated') !== true) {
        element.html(`<span class="price-value -large">${showCurrencyCode ? window.ACC.currencyCode : ''}&nbsp--</span>`);
      }
      return;
    }

    const regularPriceFormated = formatPrice(regularPrice, showCurrencyCode);

    if (promotionPrice) {
      const promotionPriceFormatted = formatPrice(promotionPrice, showCurrencyCode);
      element.html(`
        <span class="price-value -large -promotion">${promotionPriceFormatted}</span>
        <span class="price-value -crossed-out">${regularPriceFormated}</span>
      `);
    } else {
      element.html(`<span class="price-value -large">${regularPriceFormated}</span>`);
    }
    element.data('price-populated', true);
  }

  // eslint-disable-next-line class-methods-use-this
  private removePriceLoader(): void {
    $(selectors.value).each((_, el) => {
      const element = $(el);
      const showCurrencyCode = element.data('currency-code') as boolean;
      if (element.data('price-populated') !== true) {
        element.html(`<span class="price-value -large">${showCurrencyCode ? window.ACC.currencyCode : ''}&nbsp--</span>`);
      }
    });
  }
}
